import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./game-tooltip.css";

type HintAnchor = HTMLElement | SVGElement;
type Hint = { anchor: HintAnchor; text: string };

/** One shared hint layer for controls carrying data-tooltip. Accessible names
 * remain on the controls themselves; this layer supplies extra guidance. */
export function GameTooltip() {
  const id = useId();
  const [hint, setHint] = useState<Hint | null>(null);
  const [position, setPosition] = useState({ left: 0, top: 0, ready: false });
  const bubble = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let keyboard = true;
    let anchor: HintAnchor | null = null;
    const dismiss = () => {
      anchor = null;
      setHint(null);
    };
    const targetControl = (target: EventTarget | null) =>
      target instanceof Element
        ? target.closest<HintAnchor>("[data-tooltip]")
        : null;
    const show = (control: HintAnchor | null) => {
      const text = control?.dataset.tooltip?.trim();
      if (!control || !text || control.closest("[inert], [hidden]")) return;
      if (control === anchor) return;
      anchor = control;
      setPosition({ left: 0, top: 0, ready: false });
      setHint({ anchor: control, text });
    };
    const pointerOver = (event: PointerEvent) => {
      if (
        event.pointerType !== "mouse" ||
        !window.matchMedia("(hover: hover)").matches
      )
        return;
      show(targetControl(event.target));
    };
    const pointerOut = (event: PointerEvent) => {
      if (
        anchor?.contains(event.target as Node) &&
        !anchor.contains(event.relatedTarget as Node | null)
      )
        dismiss();
    };
    const focusIn = (event: FocusEvent) => {
      if (keyboard) show(targetControl(event.target));
    };
    const focusOut = (event: FocusEvent) => {
      if (
        anchor?.contains(event.target as Node) &&
        !anchor.contains(event.relatedTarget as Node | null)
      )
        dismiss();
    };
    const pointerDown = () => {
      keyboard = false;
      dismiss();
    };
    const keyDown = (event: KeyboardEvent) => {
      keyboard = true;
      if (event.key === "Escape") dismiss();
    };
    document.addEventListener("pointerover", pointerOver);
    document.addEventListener("pointerout", pointerOut);
    document.addEventListener("pointerdown", pointerDown, true);
    document.addEventListener("focusin", focusIn);
    document.addEventListener("focusout", focusOut);
    document.addEventListener("keydown", keyDown, true);
    document.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    window.addEventListener("blur", dismiss);
    return () => {
      document.removeEventListener("pointerover", pointerOver);
      document.removeEventListener("pointerout", pointerOut);
      document.removeEventListener("pointerdown", pointerDown, true);
      document.removeEventListener("focusin", focusIn);
      document.removeEventListener("focusout", focusOut);
      document.removeEventListener("keydown", keyDown, true);
      document.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
      window.removeEventListener("blur", dismiss);
    };
  }, []);

  useLayoutEffect(() => {
    if (!hint || !bubble.current) return;
    const rect = hint.anchor.getBoundingClientRect();
    const bounds = bubble.current.getBoundingClientRect();
    const margin = 12;
    const maxLeft = Math.max(margin, window.innerWidth - bounds.width - margin);
    const left = Math.min(
      maxLeft,
      Math.max(margin, rect.left + rect.width / 2 - bounds.width / 2),
    );
    const above = rect.top - bounds.height - 10;
    const top = Math.max(
      margin,
      Math.min(
        above >= margin ? above : rect.bottom + 10,
        window.innerHeight - bounds.height - margin,
      ),
    );
    setPosition({ left, top, ready: true });
    const descriptions = hint.anchor.getAttribute("aria-describedby");
    hint.anchor.setAttribute(
      "aria-describedby",
      [descriptions, id].filter(Boolean).join(" "),
    );
    return () => {
      if (descriptions === null)
        hint.anchor.removeAttribute("aria-describedby");
      else hint.anchor.setAttribute("aria-describedby", descriptions);
    };
  }, [hint, id]);

  if (!hint) return null;
  const host = hint.anchor.closest("dialog[open]") ?? document.body;
  return createPortal(
    <div
      id={id}
      ref={bubble}
      role="tooltip"
      className={`gameTooltip${hint.anchor.closest(".highContrast") ? " gameTooltip--highContrast" : ""}`}
      style={{
        left: position.left,
        top: position.top,
        visibility: position.ready ? "visible" : "hidden",
      }}
    >
      <span className="gameTooltip__signal" aria-hidden="true" />
      {hint.text}
    </div>,
    host,
  );
}
